<template>
  <div class="flex flex-col justify-start items-start gap-0">
    <LabelReset
      ref="reference"
      :initialValue="initialDatetime"
      :modelValue="modelValue"
      @update:modelValue="resetToInitial"
    >
      <slot name="label" />
    </LabelReset>
    <span
      class="dateTimeLabelContainer text-lg font-semibold font-heading text-fg-link-primary cursor-pointer flex justify-start items-center gap-3"
      @click="isDateTimePickerOpen = !isDateTimePickerOpen"
    >
      <slot name="dateTimeLabel" v-bind="scopedSlotVariables" />
      <CheckCircleIcon v-if="scopedSlotVariables.hasOccurred" class="checkCircleIcon w-6 h-6" />
      <PencilSquareIcon v-else class="pencilSquareIcon w-6 h-6" />
    </span>
    <div class="footerContainer text-sm text-fg-secondary leading-4 mt-2">
      <slot name="footer" v-bind="scopedSlotVariables" />
    </div>

    <!--MODAL PICKER-->
    <Teleport to="body">
      <OnClickOutside
        id="modalPicker"
        v-if="isDateTimePickerOpen"
        @trigger="isDateTimePickerOpen = false"
        ref="floating"
        :style="floatingStyles"
        class="z-10 shadow-podHover bg-surface-primary rounded w-80 border border-linear-subtle"
      >
        <DatePicker
          id="date-picker"
          class="shadow-none"
          :selectedDate="date"
          @datePickerChange="dateUpdate($event.date)"
        />
        <div class="flex justify-start items-stretch gap-4 px-4 pb-6 h-16">
          <TextInput
            id="time-picker"
            type="time"
            classes="w-3/5"
            :modelValue="time"
            @update:modelValue="timeUpdate"
          />
          <SlotButton id="nowButton" @click="setNow" alt>Now</SlotButton>
        </div>
        <div class="border-t border-linear-subtle px-6 py-4">
          <span class="venueDateInfo text-sm text-fg-secondary flex flex-wrap gap-x-2">
            <span>Date & time at {{ venue_name }} now:</span>
            <span class="font-semibold">{{ currentTimeAtVenue }}</span>
          </span>
        </div>
      </OnClickOutside>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue'
import { OnClickOutside } from '@vueuse/components'
import { useIntervalFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, toRefs } from 'vue'

import CheckCircleIcon from '@/assets/checkCircle.svg?component'
import PencilSquareIcon from '@/assets/pencilSquare.svg?component'
import SlotButton from '@/common/components/atoms/SlotButton.vue'
import LabelReset from '@/common/components/molecules/LabelReset.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import DatePicker from '@/common/components/organisms/DatePicker.vue'
import { useSettingStore } from '@/stores/setting'

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'update:modelValue', value: string): void
}>()
const props = defineProps<{
  modelValue: string | null
  initialDatetime: string | null
}>()
const { modelValue } = toRefs(props)
const safeModelValue = computed(() => modelValue.value ?? settingStore.nowDateTime())
const date = computed(() => settingStore.dateIsoStr(safeModelValue.value))
const time = computed(() => settingStore.time24Str(safeModelValue.value))

const settingStore = useSettingStore()
const { venue_name } = storeToRefs(settingStore)
const currentTimeAtVenue = ref(settingStore.dateTimeStr(settingStore.nowDateTime()))
useIntervalFn(() => {
  currentTimeAtVenue.value = settingStore.dateTimeStr(settingStore.nowDateTime())
}, 1000)

const scopedSlotVariables = computed(() => ({
  hasOccurred: settingStore.hasDateTimePast(safeModelValue.value),
  dateTimeStr: settingStore.dateTimeStr(safeModelValue.value),
  timeFromNow: settingStore.fromNowStr(safeModelValue.value),
}))

const isDateTimePickerOpen = ref(false)
const reference = ref(null)
const floating = ref(null)
const { floatingStyles } = useFloating(reference, floating, {
  placement: 'left-start',
  middleware: [offset(30), flip({ fallbackAxisSideDirection: 'start' }), shift()],
  whileElementsMounted: autoUpdate,
})

function resetToInitial(initialDatetime: string) {
  emit('update:modelValue', initialDatetime)
}
function updateDateTime(payload: { date: string; time: string }) {
  emit('update:modelValue', settingStore.makeDateTime(payload))
}
function dateUpdate(date: string) {
  updateDateTime({ date, time: time.value })
}
function timeUpdate(time: string) {
  updateDateTime({ date: date.value, time })
}
function setNow() {
  const today = settingStore.nowDateTime()
  const date = settingStore.dateIsoStr(today)
  const time = settingStore.time24Str(today)
  updateDateTime({ date, time })
}
</script>
